import React from 'react';
import { Box, Button, HStack, Icon, Link, Text, VStack } from '@chakra-ui/react';
import { DocumentWithDownloadUrl as Document } from '@/client';
import { displayRecordName, isImportant, isLink, isNote } from '@/utils/documentUtils.ts';
import { LuCircleAlert, LuExternalLink, LuFileText, LuNotepadText } from 'react-icons/lu';

interface SemanticSourceCardProps {
	index: number;
	label: string;
	document: Document | undefined;
	url: string | undefined;
	isHighlighted: boolean;
	canDownloadDocuments: boolean;
	onOpenDocument: (doc: Document, pages?: number[]) => void;
}

const getSemanticSourceMeta = (document: Document | undefined): { eyebrow: string; icon: typeof LuFileText } => {
	if (!document) return { eyebrow: 'Source', icon: LuFileText };
	if (isNote(document)) return { eyebrow: 'Note', icon: LuNotepadText };
	if (isImportant(document)) return { eyebrow: 'Important systems', icon: LuCircleAlert };
	if (isLink(document)) return { eyebrow: 'Link', icon: LuExternalLink };
	return { eyebrow: 'Source', icon: LuFileText };
};

const getDescriptionPreview = (document: Document | undefined): string | null => {
	if (!document?.description || isLink(document) || isImportant(document)) return null;
	const lines = document.description
		.split('\n')
		.map((line) => line.trim())
		.filter(Boolean);
	return lines.slice(1).join(' ') || null;
};

// TODO: When source metadata can expose structured colour values, render cited paint colours as
// small swatches beside the label/description instead of trying to infer named colours in the UI.
const SemanticSourceCard: React.FC<SemanticSourceCardProps> = (props: SemanticSourceCardProps) => {
	const { index, label, document, url, isHighlighted, canDownloadDocuments, onOpenDocument } = props;
	const meta = getSemanticSourceMeta(document);
	const title = document ? displayRecordName(document) : label;
	const description = getDescriptionPreview(document);
	const canOpenDocument = !!document;

	return (
		<Box
			flex="1"
			minW={0}
			py={3}
			borderTopWidth={index === 0 ? 0 : '1px'}
			borderColor={isHighlighted ? 'border.strong' : 'border.default'}
			bg={isHighlighted ? 'brand.muted' : undefined}
		>
			<HStack gap={3} align="flex-start" w="full" minW={0}>
				<Box
					display="inline-flex"
					alignItems="center"
					justifyContent="center"
					boxSize="36px"
					flexShrink={0}
					borderRadius="sm"
					borderWidth="1px"
					borderColor="border.default"
					bg="surface.subtle"
					color="fg.muted"
				>
					<Icon as={meta.icon} boxSize={4} />
				</Box>
				<VStack gap={1} align="flex-start" minW={0} flex="1">
					<Text textStyle="label.sm" color="fg.muted">
						{meta.eyebrow}
					</Text>
					{canOpenDocument ? (
						<Button
							type="button"
							variant="plain"
							h="auto"
							minW={0}
							w="full"
							overflow="hidden"
							p={0}
							justifyContent="flex-start"
							textAlign="left"
							textStyle="label"
							_hover={{ color: 'brand.fg', textDecoration: 'underline' }}
							onClick={() => onOpenDocument(document)}
						>
							<Text as="span" w="full" truncate title={title}>
								{title}
							</Text>
						</Button>
					) : url && canDownloadDocuments ? (
						<Link
							href={url}
							target="_blank"
							rel="noopener noreferrer"
							display="block"
							w="full"
							truncate
							title={title}
						>
							{title}
						</Link>
					) : (
						<Text textStyle="label" w="full" truncate title={title}>
							{title}
						</Text>
					)}
					{description && (
						<Text fontSize="xs" color="fg.muted" lineClamp={2}>
							{description}
						</Text>
					)}
				</VStack>
			</HStack>
		</Box>
	);
};

export default SemanticSourceCard;
